import React, { useState } from 'react'
import { ComponentGrade } from '../types'
import {
  ReportingContainer,
  ReportTitle,
  NavTabs,
  NavTab,
  StatsGrid,
  SuccessStatCard,
  InfoStatCard,
  CautionStatCard,
  StatValue,
  StatLabel,
  ContentSection,
  SectionTitle,
  ReportTable,
  TableHeader,
  TableCell,
  StatusBadge
} from './Reporting.styles'

const Reporting: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState<string>('yield')

  // Mock data for demonstration
  const yieldData = {
    totalReturned: 150,
    totalRebuilt: 120,
    yieldPercentage: 80,
    gradeDistribution: [
      { grade: ComponentGrade.NEW, count: 45, percentage: 30 },
      { grade: ComponentGrade.GRADE_A, count: 60, percentage: 40 },
      { grade: ComponentGrade.GRADE_B, count: 30, percentage: 20 },
      { grade: ComponentGrade.GRADE_C, count: 15, percentage: 10 }
    ]
  }

  const scrapData = {
    totalParts: 500,
    scrapParts: 75,
    scrapPercentage: 15,
    scrapByType: [
      { type: 'Battery', count: 25, percentage: 33.3 },
      { type: 'Display', count: 20, percentage: 26.7 },
      { type: 'Housing', count: 15, percentage: 20 },
      { type: 'Wand', count: 10, percentage: 13.3 },
      { type: 'Other', count: 5, percentage: 6.7 }
    ]
  }

  const financialData = {
    rebuildCost: 15670.50,
    rebuildRevenue: 28950.00,
    newProductionCost: 45000.00,
    savings: 13279.50,
    roiPercentage: 84.7
  }

  const inventoryData = {
    totalValue: 125430.75,
    itemsByGrade: [
      { grade: ComponentGrade.NEW, count: 150, value: 45200.25 },
      { grade: ComponentGrade.GRADE_A, count: 280, value: 38670.80 },
      { grade: ComponentGrade.GRADE_B, count: 195, value: 25890.45 },
      { grade: ComponentGrade.GRADE_C, count: 120, value: 12569.25 },
      { grade: ComponentGrade.SCRAP, count: 45, value: 3100.00 }
    ]
  }

  const workOrderStats = {
    totalOrders: 85,
    completedOrders: 72,
    inProgressOrders: 8,
    cancelledOrders: 5,
    avgCompletionTime: 2.5, // hours
    completionRate: 84.7
  }

  const renderYieldReport = () => (
    <ContentSection>
      <SectionTitle>Yield Analysis Report</SectionTitle>
      <StatsGrid>
        <InfoStatCard>
          <StatValue $color="#1976d2">
            {yieldData.totalReturned}
          </StatValue>
          <StatLabel>Total Returned Kits</StatLabel>
        </InfoStatCard>
        <SuccessStatCard>
          <StatValue $color="#4caf50">
            {yieldData.totalRebuilt}
          </StatValue>
          <StatLabel>Successfully Rebuilt</StatLabel>
        </SuccessStatCard>
        <CautionStatCard>
          <StatValue $color="#ff9800">
            {yieldData.yieldPercentage}%
          </StatValue>
          <StatLabel>Yield Rate</StatLabel>
        </CautionStatCard>
      </StatsGrid>

      <ContentSection>
        <h4>Grade Distribution</h4>
        <ReportTable>
          <thead>
            <tr>
              <TableHeader>Grade</TableHeader>
              <TableHeader>Count</TableHeader>
              <TableHeader>Percentage</TableHeader>
              <TableHeader>Visual</TableHeader>
            </tr>
          </thead>
          <tbody>
            {yieldData.gradeDistribution.map(item => (
              <tr key={item.grade}>
                <TableCell>
                  <StatusBadge $variant={getGradeBadgeVariant(item.grade)}>
                    {item.grade.replace('_', ' ').toUpperCase()}
                  </StatusBadge>
                </TableCell>
                <TableCell>{item.count}</TableCell>
                <TableCell>{item.percentage}%</TableCell>
                <TableCell>
                  <div style={{ 
                    width: `${item.percentage * 2}px`, 
                    height: '10px', 
                    backgroundColor: getGradeColor(item.grade),
                    borderRadius: '5px' 
                  }}></div>
                </TableCell>
              </tr>
            ))}
          </tbody>
        </ReportTable>
      </ContentSection>
    </ContentSection>
  )

  const renderScrapReport = () => (
    <div>
      <h3>Scrap Analysis Report</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ background: '#ffebee', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f44336' }}>
            {scrapData.scrapParts}
          </div>
          <div style={{ color: '#666' }}>Scrapped Parts</div>
        </div>
        <div style={{ background: '#fff3e0', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff9800' }}>
            {scrapData.scrapPercentage}%
          </div>
          <div style={{ color: '#666' }}>Scrap Rate</div>
        </div>
        <div style={{ background: '#f3e5f5', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#9c27b0' }}>
            {scrapData.totalParts - scrapData.scrapParts}
          </div>
          <div style={{ color: '#666' }}>Recoverable Parts</div>
        </div>
      </div>

      <div className="form-section">
        <h4>Scrap by Part Type</h4>
        <table className="table">
          <thead>
            <tr>
              <th>Part Type</th>
              <th>Scrapped Count</th>
              <th>Percentage of Total Scrap</th>
              <th>Visual</th>
            </tr>
          </thead>
          <tbody>
            {scrapData.scrapByType.map(item => (
              <tr key={item.type}>
                <td><strong>{item.type}</strong></td>
                <td>{item.count}</td>
                <td>{item.percentage}%</td>
                <td>
                  <div style={{ 
                    width: '100px', 
                    height: '20px', 
                    background: '#f0f0f0', 
                    borderRadius: '10px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${item.percentage}%`, 
                      height: '100%', 
                      background: '#f44336',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderFinancialReport = () => (
    <div>
      <h3>Financial Performance Report</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ background: '#e8f5e8', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4caf50' }}>
            ${financialData.rebuildRevenue.toLocaleString()}
          </div>
          <div style={{ color: '#666' }}>Rebuild Revenue</div>
        </div>
        <div style={{ background: '#fff3e0', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ff9800' }}>
            ${financialData.rebuildCost.toLocaleString()}
          </div>
          <div style={{ color: '#666' }}>Rebuild Cost</div>
        </div>
        <div style={{ background: '#e3f2fd', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1976d2' }}>
            ${financialData.savings.toLocaleString()}
          </div>
          <div style={{ color: '#666' }}>Cost Savings</div>
        </div>
        <div style={{ background: '#f3e5f5', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#9c27b0' }}>
            {financialData.roiPercentage}%
          </div>
          <div style={{ color: '#666' }}>ROI</div>
        </div>
      </div>

      <div className="form-section">
        <h4>Cost Comparison</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
          <div>
            <h5 style={{ color: '#4caf50' }}>Rebuild Process</h5>
            <div>Revenue: ${financialData.rebuildRevenue.toLocaleString()}</div>
            <div>Cost: ${financialData.rebuildCost.toLocaleString()}</div>
            <div style={{ fontWeight: 'bold', color: '#4caf50' }}>
              Profit: ${(financialData.rebuildRevenue - financialData.rebuildCost).toLocaleString()}
            </div>
          </div>
          <div>
            <h5 style={{ color: '#ff9800' }}>New Production Alternative</h5>
            <div>Estimated Cost: ${financialData.newProductionCost.toLocaleString()}</div>
            <div style={{ fontWeight: 'bold', color: '#4caf50' }}>
              Savings: ${financialData.savings.toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderInventoryReport = () => (
    <div>
      <h3>Inventory Visibility Report</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ background: '#e3f2fd', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1976d2' }}>
            ${inventoryData.totalValue.toLocaleString()}
          </div>
          <div style={{ color: '#666' }}>Total Inventory Value</div>
        </div>
        <div style={{ background: '#e8f5e8', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4caf50' }}>
            {inventoryData.itemsByGrade.reduce((sum, item) => sum + item.count, 0)}
          </div>
          <div style={{ color: '#666' }}>Total Items</div>
        </div>
      </div>

      <div className="form-section">
        <h4>Inventory by Grade</h4>
        <table className="table">
          <thead>
            <tr>
              <th>Grade</th>
              <th>Item Count</th>
              <th>Total Value</th>
              <th>Avg Value per Item</th>
              <th>Distribution</th>
            </tr>
          </thead>
          <tbody>
            {inventoryData.itemsByGrade.map(item => {
              const avgValue = item.count > 0 ? item.value / item.count : 0
              const totalItems = inventoryData.itemsByGrade.reduce((sum, i) => sum + i.count, 0)
              const percentage = (item.count / totalItems) * 100

              return (
                <tr key={item.grade}>
                  <td>
                    <span className={`status-badge ${getGradeColor(item.grade)}`}>
                      {item.grade.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td><strong>{item.count}</strong></td>
                  <td>${item.value.toLocaleString()}</td>
                  <td>${avgValue.toFixed(2)}</td>
                  <td>
                    <div style={{ 
                      width: '100px', 
                      height: '20px', 
                      background: '#f0f0f0', 
                      borderRadius: '10px',
                      overflow: 'hidden'
                    }}>
                      <div style={{ 
                        width: `${percentage}%`, 
                        height: '100%', 
                        background: '#1976d2',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderWorkOrderReport = () => (
    <div>
      <h3>Work Order Performance Report</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ background: '#e3f2fd', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1976d2' }}>
            {workOrderStats.totalOrders}
          </div>
          <div style={{ color: '#666' }}>Total Orders</div>
        </div>
        <div style={{ background: '#e8f5e8', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4caf50' }}>
            {workOrderStats.completedOrders}
          </div>
          <div style={{ color: '#666' }}>Completed</div>
        </div>
        <div style={{ background: '#fff3e0', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff9800' }}>
            {workOrderStats.inProgressOrders}
          </div>
          <div style={{ color: '#666' }}>In Progress</div>
        </div>
        <div style={{ background: '#f3e5f5', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#9c27b0' }}>
            {workOrderStats.avgCompletionTime}h
          </div>
          <div style={{ color: '#666' }}>Avg Completion</div>
        </div>
        <div style={{ background: '#e0f7fa', padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#00796b' }}>
            {workOrderStats.completionRate}%
          </div>
          <div style={{ color: '#666' }}>Success Rate</div>
        </div>
      </div>
    </div>
  )

  const getGradeColor = (grade: ComponentGrade) => {
    switch (grade) {
      case ComponentGrade.NEW:
      case ComponentGrade.UNUSED:
        return '#1976d2' // Blue
      case ComponentGrade.GRADE_A:
        return '#4caf50' // Green
      case ComponentGrade.GRADE_B:
        return '#ff9800' // Orange
      case ComponentGrade.GRADE_C:
        return '#ffc107' // Yellow
      case ComponentGrade.SCRAP:
        return '#f44336' // Red
      default:
        return '#1976d2'
    }
  }

  const getGradeBadgeVariant = (grade: ComponentGrade): 'new' | 'grade-a' | 'grade-b' | 'grade-c' | 'scrap' => {
    switch (grade) {
      case ComponentGrade.NEW:
      case ComponentGrade.UNUSED:
        return 'new'
      case ComponentGrade.GRADE_A:
        return 'grade-a'
      case ComponentGrade.GRADE_B:
        return 'grade-b'
      case ComponentGrade.GRADE_C:
        return 'grade-c'
      case ComponentGrade.SCRAP:
        return 'scrap'
      default:
        return 'new'
    }
  }

  const renderSelectedReport = () => {
    switch (selectedReport) {
      case 'yield':
        return renderYieldReport()
      case 'scrap':
        return renderScrapReport()
      case 'financial':
        return renderFinancialReport()
      case 'inventory':
        return renderInventoryReport()
      case 'workorders':
        return renderWorkOrderReport()
      default:
        return renderYieldReport()
    }
  }

  return (
    <ReportingContainer>
      <ReportTitle>Reporting & Analytics</ReportTitle>
      
      <NavTabs>
        <NavTab 
          $active={selectedReport === 'yield'}
          onClick={() => setSelectedReport('yield')}
        >
          📊 Yield Analysis
        </NavTab>
        <NavTab 
          $active={selectedReport === 'scrap'}
          onClick={() => setSelectedReport('scrap')}
        >
          🗑️ Scrap Analysis
        </NavTab>
        <NavTab 
          $active={selectedReport === 'financial'}
          onClick={() => setSelectedReport('financial')}
        >
          💰 Financial
        </NavTab>
        <NavTab 
          $active={selectedReport === 'inventory'}
          onClick={() => setSelectedReport('inventory')}
        >
          📦 Inventory
        </NavTab>
        <NavTab 
          $active={selectedReport === 'workorders'}
          onClick={() => setSelectedReport('workorders')}
        >
          🔨 Work Orders
        </NavTab>
      </NavTabs>

      {renderSelectedReport()}
    </ReportingContainer>
  )
}

export default Reporting